import React, { useId, useState } from 'react';
import { Autocomplete, Popper, Paper, Box, TextField, InputAdornment, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import GlassmorphismDesignSystem from '../theme/GlassmorphismDesignSystem';

const Select = ({
    label,
    value,
    onChange,
    fieldKey,
    selections = [],
    error,
    help,
    required,
    field,
    fullWidth = true,
    noEmpty = false,
    addNew = false,
    disabled = false,
    filterValue,
    defaultValue,
    groupBy,
    freeSolo = false,
    ...props
}) => {
    const generatedId = useId();
    const inputId = props.id || generatedId;
    const { t, i18n } = useTranslation();
    const currentLangCode = i18n.language;
    const theme = useTheme();
    const isDarkmode = theme.palette.mode === 'dark';
    const [open, setOpen] = useState(false);
    let data;

    if (!data) {
        data = [];
    }


    // Saisie libre : les sélections ne sont que des suggestions, vider le champ suffit à « vider » la valeur.
    data = freeSolo
        ? selections
        : noEmpty
        ? addNew
            ? [...selections, { label: t('addNew'), value: 'ID' }]
            : selections
        : addNew
            ? [
                ...selections,
                { label: t('empty'), value: '' },
                { label: t('addNew'), value: 'ID' },
            ]
            : [...selections, { label: t('empty'), value: '' }];

    const valuesEqual = (a, b) =>
        a === b || (a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b));
    const selectedValue = freeSolo
        ? null
        : data?.find((option) => valuesEqual(option?.value, value) || option?.label === value);

    // Le texte saisi EST la valeur : le champ est piloté par `inputValue`, la sélection d'une suggestion
    // passe par onInputChange (raison « reset ») et ne doit pas repasser par onChange.
    const freeSoloProps = freeSolo
        ? {
            freeSolo: true,
            value: null,
            inputValue: value == null ? '' : String(value),
            onInputChange: (_event, text, reason) => {
                if (reason === 'input') setOpen(true);
                onChange(fieldKey, text);
            },
            onChange: () => {},
        }
        : {};


    if (filterValue) {
        data = data?.filter((option) => option?.filter?.includes(filterValue));
    }




    return (

        <Box sx={{ width: '100%', color: 'text.primary' }}>
            {label && (
                <Typography
                    component="label"
                    htmlFor={inputId}
                    sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: '2px',
                        display: 'block',
                    }}
                >
                    {label}
                    {required && ' *'}
                </Typography>
            )}
            <Autocomplete
                id={inputId}
                key={fieldKey || 'select'}
                disabled={disabled}
                open={open}
                onOpen={() => {}}
                onClose={() => setOpen(false)}
                PopperComponent={({ children, ...props }) => (
                    <Popper {...props} style={{ ...props.style, zIndex: 99999 }}>{children}</Popper>
                )}
                PaperComponent={({ children, ...props }) => (
                    <Paper
                        {...props}
                        sx={{
                            borderRadius: '8px',
                            overflow: 'hidden',
                            color: 'text.primary',
                            bgcolor: 'background.paper',
                        }}
                    >
                        {children}
                    </Paper>
                )}
                ListboxProps={{
                    sx: {
                        maxHeight: 150,
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: isDarkmode ? 'rgb(51,51,51)' : '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: isDarkmode ? '#666' : '#888',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: isDarkmode ? '#888' : '#666',
                        },
                    },
                }}
                options={data}
                groupBy={groupBy}
                noOptionsText={t('noOption')}
                getOptionLabel={(option) =>
                    option?.[`label_${currentLangCode}`] ||
                    option?.label ||
                    'N/A'
                }
                value={selectedValue ?? null}
                isOptionEqualToValue={(option, selected) =>
                    valuesEqual(option.value ?? option.label, selected.value ?? selected.label)
                }
                onChange={(event, newValue) => {
                    if (newValue != null) {
                        onChange(
                            fieldKey,
                            newValue?.value ??
                            newValue?.id ??
                            newValue?.[`label_${currentLangCode}`] ??
                            newValue?.label ??
                            ''
                        );
                    }
                }}
                renderOption={({ key, ...props }, option) => (
                    <Box component="li" key={key} {...props}>
                        {option.media_url && (
                            <img
                                src={option.media_url}
                                alt=""
                                style={{ width: 30, height: 30, marginRight: 10 }}
                            />
                        )}
                        {option?.[`label_${currentLangCode}`] || option.label}
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        helperText={help}
                        disabled={disabled}
                        {...params}
                        placeholder={t('select')}
                        onClick={() => !disabled && !open && setOpen(true)}
                        defaultValue={defaultValue}
                        variant="outlined"
                        inputProps={{
                            ...params.inputProps,
                            onKeyDown: (e) => {
                                if (e.key === 'Enter' && !open) {
                                    e.preventDefault();
                                    setOpen(true);
                                    return;
                                }
                                if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !open) {
                                    return;
                                }
                                params.inputProps?.onKeyDown?.(e);
                            },
                        }}
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    {selectedValue?.media_url && (
                                        <InputAdornment position="start">
                                            <Box
                                                component="img"
                                                src={selectedValue.media_url}
                                                alt={
                                                    selectedValue?.[
                                                    `label_${currentLangCode}`
                                                    ] || selectedValue?.label
                                                }
                                                style={{
                                                    width: 30,
                                                    height: 30,
                                                    marginRight: 10,
                                                }}
                                            />
                                        </InputAdornment>
                                    )}
                                    {params.InputProps.startAdornment}
                                </>
                            ),
                        }}
                        sx={{
                            background: GlassmorphismDesignSystem.patterns.interactive.background(theme),
                            backdropFilter: GlassmorphismDesignSystem.patterns.interactive.backdropFilter,
                            boxShadow: GlassmorphismDesignSystem.patterns.interactive.boxShadow(theme),
                            borderRadius: '8px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                height: '36px',
                                color: disabled ? 'text.disabled' : 'text.primary',
                                '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main,
                                },
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: '6px 12px',
                                border: 'none',
                                '&::placeholder': {
                                  fontSize: '11px',
                                },
                            },
                        }}
                    />
                )}
                {...freeSoloProps}
                {...props}
            />
        </Box>

    );
};

export default Select;
